import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import PageHeader from "../../components/global-layout/page-header";
import {
  CameraIcon,
  CompressIcon,
  ExpandIconScreen,
  MonitorIcon,
  PhoneIcon,
  PhoneOffIcon,
  Spinner,
  UserManagmentPageHeaderIcon,
  VerticalMoreBlueIcon,
} from "../../components/svg";
import PageLoader from "../../components/UI/page-loader";
import { useTranslation } from "react-i18next";
import { useMyContext } from "../../components/my-context/MyContext";
import { useLoadMore } from "../../utils/common";
import GridToolBar from "../../components/UI/grid-toolbar";
import { userListFetch } from "../../services/authFunctions";
import Skeleton from "react-loading-skeleton";
import Authentication from "../../auth/Authentication";
import { blobServices, remoteMonitoringServices } from "../../services/InspectionOneServices";
import ScreenshotModal from "../../components/UI/screen-shot-modal/screen-shot-modal";
import { v4 as uuidv4 } from "uuid";
import RemoteMonitoringCard from "../../components/UI/remote-monitor-card/RemoteMonitoringCard";
import UserListItem from "../../components/UI/remote-monitor-card/UserListItem";
import { MicIcon, Volume2Icon, VolumeXIcon, MicOffIcon } from 'lucide-react'; // Replace 'lucide-react' with your actual icon library
import RemoteCallControls from "../../components/UI/remote-monitor-card/RemoteCallControls";
import { ToastError } from "../../components/UI/toast";
import ToastifyMessage from "../../lib/toastify";
import WebRTCManager from "../../utils/WebRTCManager";

// Helper: Convert Base64 DataURL to Blob for uploading
const dataURLtoFile = (dataurl: string, filename: string) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

interface SignalMessage {
  type: "offer" | "answer" | "candidate" | "register" | "end-call";
  source?: string;
  target?: string;
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
}

// TURN credential generator
async function generateTurnCredentials(secret: string, user: string = "testuser", ttlSeconds: number = 3600) {
  const expiry = Math.floor(Date.now() / 1000) + ttlSeconds;
  const username = `${expiry}:${user}`;

  const enc = new TextEncoder();
  const keyData = enc.encode(secret);

  const key = await window.crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-1" },
    false,
    ["sign"]
  );

  const signature = await window.crypto.subtle.sign("HMAC", key, enc.encode(username));
  const hashArray = Array.from(new Uint8Array(signature));
  const credential = btoa(hashArray.map((b) => String.fromCharCode(b)).join(""));

  return { username, credential, ttl: ttlSeconds };
}


function RemoteMonitoringView({ userUpdated, setUserUpdated }: any) {
  const { isExternalUser, setUserRole } = useMyContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const authService = Authentication();

  // --- States ---
  const [usersList, setUsersList] = useState<any>([]);
  const [galleryData, setGalleryData] = useState<any>([]);

  // Modal States
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  // STORE BASE64 PREVIEW HERE (Not the URL yet)
  const [tempScreenshotBase64, setTempScreenshotBase64] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [deleteItem, setDeleteItem] = useState<any>(null);

  // API & Pagination
  const [isLoading, setIsLoading] = useState(false);
  const [finalFetch, setFinalFetch] = useState(false);
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  const [initialLoading, setIntialLoading] = useState(true);
  const [startIndex, setStartIndex] = useState(0);
  const [sorting, setSorting] = useState<number>(0);
  const [limit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetchMore, setIsFetchMore] = useState<any>(false);
  const [pageLoader, setPageLoader] = useState(false); // Global Loader for Uploading
  const scrollGroundRef = useRef<any>(null);
  const [toolItems, setToolItems] = useState<any>([]);
  const [isSkeletonLoader, setIsSkeletonLoader] = useState(false);
  const [sortingColumnName, setSortingColumnName] = useState<string>("");
  const { ToastifyHandle } = ToastifyMessage();
  // UI States
  const [activeUser, setActiveUser] = useState<any>(null);
  const [activeGalleryItem, setactiveGalleryItem] = useState<any>(null);
  const [callStatus, setCallStatus] = useState<'idle' | 'connecting' | 'connected'>('idle');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRefreshed, setIsRefreshed] = useState<any>(false);
  const generateGuid = () => uuidv4();

  const handleError = (titleName: any, message: any) => {
    ToastifyHandle(
      <ToastError title={titleName} description={message} />,
      "error"
    );
  };

  // #region Web RTC

  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [autoSourceId, setAutoSourceId] = useState("");

  const [wsReady, setWsReady] = useState(false);
  const [cameraStarted, setCameraStarted] = useState(false);
  const [incomingCall, setIncomingCall] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [connectionState, setConnectionState] = useState("");
  const [iceConnectionState, setIceConnectionState] = useState("");

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const webrtcManagerRef = useRef<WebRTCManager | null>(null);

  const [micMuted, setMicMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);




  // Initialize WebRTC Manager
  useEffect(() => {
  // CONFIG
  var EndPoints = authService.decryptData("serviceDetails", 1);
  const COTURN_SECRET = "SHARED_SECRET";
  var WS_SERVER = EndPoints["api.web.monitoringsignaling"];
  var TURN_SERVER_IP = EndPoints["api.web.monitoringturnserver"];

    const manager = new WebRTCManager(
      {
        coturnSecret: COTURN_SECRET,
        turnServerIP: TURN_SERVER_IP,
        wsServer: WS_SERVER,
      },
      {
        onWsReady: setWsReady,
        onCameraStarted: setCameraStarted,
        onIncomingCall: setIncomingCall,
        onCallActive: setCallActive,
        onScreenSharing: setIsScreenSharing,
        onConnectionState: setConnectionState,
        onIceConnectionState: setIceConnectionState,
        onLocalStream: (stream) => {
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
        },
        onRemoteStream: (stream) => {
          if (remoteVideoRef.current) {
            if (!remoteVideoRef.current.srcObject) {
              remoteVideoRef.current.srcObject = new MediaStream();
            }

            // ⭐ Register the remote video element into the manager
            webrtcManagerRef.current?.setRemoteVideoElement(remoteVideoRef.current);

            const videoStream = remoteVideoRef.current.srcObject as MediaStream;

            if (stream) {
              // Add new tracks
              stream.getTracks().forEach(track => {
                if (!videoStream.getTracks().find(t => t.id === track.id)) {
                  videoStream.addTrack(track);
                  console.log("✅ Added track to remote video");
                }
              });

              // Play video
              remoteVideoRef.current.play().catch(err => {
                console.error("❌ Error playing remote video:", err);
              });
            } else {
              // Clear stream
              remoteVideoRef.current.srcObject = null;
            }
          }
        },
      }
    );

    setAutoSourceId(manager.getAutoSourceId());
    const userId: any = sessionStorage.getItem("userId")?.toString();
    setSourceId(userId);

    // Initialize WebSocket
    manager.initWebSocket().catch((err) => {
      console.error("❌ Failed to initialize WebSocket:", err);
    });

    webrtcManagerRef.current = manager;

    // Cleanup on unmount
    return () => {
      manager.cleanup();
    };
  }, []);

  // Update manager when IDs change
  useEffect(() => {
    if (webrtcManagerRef.current) {
      webrtcManagerRef.current.setSourceId(sourceId);
    }
  }, [sourceId]);

  useEffect(() => {
    if (webrtcManagerRef.current) {
      webrtcManagerRef.current.setTargetId(targetId);
    }
  }, [targetId]);

  const handleStartCamera = async (withVideo: boolean) => {
    try {
      await webrtcManagerRef.current?.startCamera(withVideo);
    } catch (err) {
      alert("Camera error: " + err);
    }
  };

  const handleStartScreenShare = async () => {
    try {
      await webrtcManagerRef.current?.startScreenShare();
    } catch (err) {
      alert("Screen share error: " + err);
    }
  };

  const handleCall = async () => {
    try {
      await webrtcManagerRef.current?.call();
    } catch (err) {
      alert("Call error: " + err);
    }
  };

  const handleAttendCall = async () => {
    try {
      await webrtcManagerRef.current?.attendCall();
    } catch (err) {
      alert("Attend call error: " + err);
    }
  };

  const handleStopCall = () => {
    webrtcManagerRef.current?.stopCall();
  };

  const toggleMic = () => {
    const newState = !micMuted;
    setMicMuted(newState);
    webrtcManagerRef.current?.muteMic(newState);
  };

  const toggleSpeaker = () => {
    const newState = !speakerMuted;
    setSpeakerMuted(newState);
    webrtcManagerRef.current?.muteSpeaker(newState);
  };

  // #endregion

  // #region Effects
  useEffect(() => {
    const tools = ["refresh"];
    setToolItems(tools);
  }, []);

  useEffect(() => {
    if (callStatus === "idle") {
      listFetch("");
    }
  }, [isRefreshed, startIndex, callStatus]);
  // #endregion

  // #region API Call (User List)
  const listFetch = async (query: any) => {
    setIsLoading(true);
    setIsSkeletonLoader(true);
    try {
      const result = await userListFetch(
        query,
        startIndex,
        limit,
        sorting,
        sortingColumnName
      );
      if (result?.success) {
        const responseData = result.data?.listDetails || [];
        const mappedUsers = responseData.map((user: any) => ({
          id: user.uniqueId,
          userName: `${user.firstName} ${user.lastName}`,
          email: user.email,
          image: user.profileImageUrl,
          phone: user.mobile,
          role: user.roleName,
        }));
        if (startIndex === 0) setUsersList(mappedUsers);
        else setUsersList((prev: any) => [...prev, ...mappedUsers]);

        setTotalCount(result.data?.count || 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      setIsSkeletonLoader(false);
    }
  };
  // #endregion

  // #region Handlers
  const handleCallUser = (e: React.MouseEvent, user: any) => {
    console.log("user", user);

    setActiveUser(user);
    setTargetId(user.id);
    e.stopPropagation();
    if (activeUser?.id === user.id) {
      if (callStatus !== "idle") {
        endCall();
      } else {
        startCall(user);
      }
    } else {
      startCall(user);
    }
  };

  const startCall = (user: any) => {
    setCallStatus("connecting");
    setTimeout(() => {
      handleStartCamera(false);
    }, 1500);

  };

  useEffect(() => {
    if (cameraStarted && activeUser && sourceId != "" && targetId != "") {
      handleCall();
    }
  }, [cameraStarted, activeUser])

  const endCall = () => {
    setActiveUser(null);
    handleStopCall();
    setGalleryData([]);
    setCallStatus("idle");
    setIsExpanded(false);
  };

  // --- 1. CAPTURE & SHOW PREVIEW (No Upload Yet) ---
  const handleTakeScreenshot = () => {

    if (!remoteVideoRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.width = remoteVideoRef.current.videoWidth;
    canvas.height = remoteVideoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      // Draw frame
      ctx.drawImage(remoteVideoRef.current, 0, 0, canvas.width, canvas.height);

      // Convert to Base64 PNG for local preview
      const dataUrl = canvas.toDataURL("image/png");

      setTempScreenshotBase64(dataUrl); // Store Base64
      setShowScreenshotModal(true);     // Open Modal
    }
  };

  const directUpdateGalleryItem = async (requestBody: any, isEditMode: boolean) => {
    const saveResponse = await remoteMonitoringServices.RemoteMonitoringCapturesSaveService(requestBody);

    if (saveResponse?.success == true) {

      if (isEditMode) {
        setGalleryData((prev: any) =>
          prev.map((item: any) =>
            item.uniqueId === requestBody.uniqueId
              ? { ...item, description: requestBody.description }
              : item
          )
        );
      } else {
        setGalleryData((prev: any) => [requestBody, ...prev]);
      }
      setShowScreenshotModal(false);
      setTempScreenshotBase64(null);
      setEditItem(null);
    } else {
      handleError("Save API Failed:", saveResponse?.data?.responseMessage);
    }
  };

  const handleSaveScreenshot = async (description: string) => {
    setPageLoader(true);

    const userId = sessionStorage.getItem("userId");
    const userName: any = authService.decryptData("UserDetails", 2);;


    try {

      if (editItem) {
        const requestPayload = {
          ...editItem,
          description: description,
          actionMode: 1,
        };

        await directUpdateGalleryItem(requestPayload, true);

        setPageLoader(false);
        return;
      }

      if (!tempScreenshotBase64 || !activeUser) {
        setPageLoader(false);
        return;
      }

      const fileName = `capture_${Date.now()}.png`;
      const fileObj = dataURLtoFile(tempScreenshotBase64, fileName);

      const reader = new FileReader();

      reader.onload = async (e: ProgressEvent<FileReader>) => {
        if (e.target && e.target.result) {
          const binaryData = e.target.result as ArrayBuffer;
          try {
            const blobResponse = await blobServices.BlobStorage(
              binaryData,
              "png",
              fileName
            );

            if (blobResponse?.success == true) {
              const uploadedUrl = blobResponse.data?.fileUrl;

              if (uploadedUrl) {
                const requestPayload = {
                  uniqueId: generateGuid(),
                  sourceUserId: userId,
                  sourceUserName: (userName?.firstName + " " + userName?.lastName).trim(),
                  destinationUserId: activeUser.id,
                  url: uploadedUrl,
                  description: description,
                  actionMode: 1,
                  destinationUserName: activeUser.userName,
                  date: new Date().toLocaleDateString(),
                  time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                };
                await directUpdateGalleryItem(requestPayload, false);

              } else {
                console.error("Blob URL missing");
              }
            } else {
              console.error("Blob Upload Failed", blobResponse);
            }
          } catch (err) {
            handleError("Upload Failed:", err);
          } finally {
            setPageLoader(false);
          }
        }
      };

      reader.readAsArrayBuffer(fileObj);

    } catch (error) {
      handleError("Upload Failed:", error);
      setPageLoader(false);
    }
  };

  const handleCloseModal = () => {
    setTempScreenshotBase64(null);
    setShowScreenshotModal(false);
  };

  const handleEditItem = (item: any) => {
    setEditItem(item);
    setShowScreenshotModal(true);
  };

  const handleDeleteItem = async (item: any) => {
    setPageLoader(true);
    try {
      const userId = sessionStorage.getItem("userId");
      const requestPayload = {
        ...item,
        actionMode: 2,
      };
      const response = await remoteMonitoringServices.RemoteMonitoringCapturesSaveService(requestPayload);

      if (response?.success === true) {
        setGalleryData((prev: any) => prev.filter((d: any) => d.uniqueId !== item.uniqueId));
      } else {
        handleError("Delete Failed:", response?.data?.responseMessage);
      }
    } catch (err) {
      handleError("Delete Failed:", err);
    } finally {
      setPageLoader(false);
    }
  };

  const handleDownloadItem = (item: any) => {
    if (item.url) {
      const link = document.createElement('a');
      link.href = item.url;
      link.download = `capture_${item.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleToggleExpand = () => setIsExpanded(!isExpanded);
  const handleRefreshFn = () => {
    setStartIndex(0);
    setIsRefreshed(!isRefreshed);
  };
  const handleMenuClick = (e: React.MouseEvent, user: any) => {
    e.stopPropagation();
    console.log("Menu clicked for", user);
  };

  const { handleScroll } = useLoadMore(
    startIndex,
    limit,
    totalCount,
    setStartIndex,
    setIsFetchMore,
    () => { },
    scrollGroundRef
  );

  const breadcrumbs = [
    { label: "Remote Monitoring", link: "/remote-monitoring" },
    { label: "Remote Monitoring View", link: undefined },
  ];
  // #endregion


  // In your React component, add this to see all state changes:
  useEffect(() => {
    if (callActive) {
      setCallStatus("connected");
    }
    else {
      setCallStatus("idle");
      setIsExpanded(false);
    }
  }, [callActive]);

  return (
    <>
      <div className="w-full h-full flex flex-col bg-io-white dark:bg-io-black-15">
        <PageHeader
          title={"Remote Monitoring View"}
          breadcrumbs={breadcrumbs}
          icon={<MonitorIcon />}
        />

        <div className="w-full flex-1 overflow-hidden flex flex-col">
          <div>
            <GridToolBar
              columnCount={totalCount}
              gridType={t("Users")}
              selectView={false}
              tools={toolItems}
              handleRefresh={handleRefreshFn}
              isExcelLoading={false}
              isSkeletonLoader={isSkeletonLoader}
            />
          </div>

          {/* --- MAIN LAYOUT --- */}
          <div className="w-full h-full flex border-t border-gray-200 dark:border-gray-700">
            {/* 1. LEFT SIDE */}
            <div className="w-[30%] h-full flex flex-col border-r border-gray-200 dark:border-gray-700 bg-io-white dark:bg-io-black-15 transition-all duration-300">
              {callStatus === "idle" ? (
                // ... (User List Render) ...
                <>
                  <div className="px-[2rem] pt-[1rem] pb-[1rem]">
                    <h3 className="text-[2rem] font-inter font-semibold text-io-black dark:text-io-white">
                      {t("Available Users")}
                    </h3>
                  </div>
                  <div className="w-full flex-1 overflow-y-auto px-[2rem]">
                    {isSkeletonLoader && startIndex === 0 ? (
                      Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="w-full h-auto flex justify-between items-center gap-4 mb-6 p-2 rounded-lg">
                          <div className="flex justify-start items-center gap-4">
                            <div className="w-[4rem] h-[4rem]"><Skeleton circle={true} height="100%" containerClassName="w-full h-full" /></div>
                            <div className="flex flex-col gap-2"><Skeleton width={120} height={15} /><Skeleton width={80} height={12} /></div>
                          </div>
                          <div className="w-[3.5rem] h-[3.5rem]"><Skeleton circle={true} height="100%" containerClassName="w-full h-full" /></div>
                        </div>
                      ))
                    ) : (
                      <>
                        {usersList.length > 0 ? (
                          usersList.map((user: any, index: number) => (
                            <UserListItem
                              key={index}
                              user={user}
                              isActive={activeUser?.id === user.id}
                              callStatus={callStatus}
                              onCall={handleCallUser}
                            />
                          ))
                        ) : (
                          <div className="text-center p-4 text-gray-500">No users found.</div>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                // ... (Gallery Render) ...
                <>
                  <div className="px-[2rem] pt-[1rem]">
                    <h3 className="text-[2rem] font-inter font-semibold text-io-black dark:text-io-white">
                      {"Connected"}
                    </h3>
                    <UserListItem
                      key={0}
                      user={activeUser}
                      isActive={true}
                      callStatus={callStatus}
                      onCall={handleCallUser}
                    />
                  </div>
                  <div className="px-[2rem] pt-[1rem] pb-[1rem] bg-io-blue-light/5 dark:bg-io-blue-dark/10 border-b border-gray-100 dark:border-gray-800">
                    <h3 className="text-[2rem] font-inter font-semibold text-io-black dark:text-io-white truncate" title={activeUser?.userName}>
                      {t("Gallery")} - {activeUser?.userName}
                    </h3>
                  </div>
                  <div ref={scrollGroundRef} onScroll={handleScroll} className="w-full flex-1 overflow-y-auto px-[2rem] py-[1rem]">
                    {galleryData.length > 0 ? (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {galleryData.map((item: any, index: number) => (
                          <RemoteMonitoringCard
                            key={item.id || index}
                            item={item}
                            isExternalUser={isExternalUser}
                            onEdit={handleEditItem}
                            onDelete={handleDeleteItem}
                            onDownload={handleDownloadItem}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-io-gray-66 gap-2">
                        <span className="text-[1.4rem]">No screenshots yet.</span>
                        <span className="text-[1.2rem]">Take a snapshot from the stream.</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* 2. RIGHT SIDE (70%) */}
            {isExpanded && <div className="w-[70%] h-full bg-black/10"></div>}

            <div className={`transition-all duration-300 ${isExpanded ? "fixed inset-0 z-[20] flex items-center justify-center bg-black/60 backdrop-blur-sm p-10" : "w-[70%] h-full relative"}`}>
              <div className={`bg-black relative flex flex-col justify-center items-center overflow-hidden ${isExpanded ? "w-[85vw] h-[85vh] rounded-[2rem] shadow-2xl border border-gray-800" : "w-full h-full"}`}>

                {/* Header Overlay */}
                {callStatus !== "idle" && (
                  <div className="absolute top-0 w-full p-6 flex justify-between items-center z-10 bg-gradient-to-b from-black/80 to-transparent">
                    <span className="text-white text-[1.6rem] font-inter font-medium flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full ${callStatus === "connected" ? "bg-red-600 animate-pulse" : "bg-yellow-500"}`}></span>
                      {callStatus === "connecting" ? "Establishing Connection..." : `Live Monitoring: ${activeUser?.userName}`}
                    </span>
                    <button onClick={() => setIsExpanded(!isExpanded)} className="text-white hover:text-io-primary p-2 rounded-full hover:bg-white/10 transition-colors">
                      {isExpanded ? <CompressIcon /> : <ExpandIconScreen />}
                    </button>
                  </div>
                )}

                {/* States */}
                {callStatus === "idle" && (
                  <div className="text-io-gray-66 flex flex-col items-center">
                    <div className="w-[8rem] h-[8rem] rounded-full bg-io-black-15 flex items-center justify-center mb-4 opacity-50"><PhoneIcon /></div>
                    <p className="text-[1.8rem] font-inter">Select a user to start monitoring</p>
                  </div>
                )}

                {callStatus === "connecting" && (
                  <div className="flex flex-col items-center text-white z-20"><Spinner /><p className="mt-6 text-[1.6rem] font-inter">Connecting...</p></div>
                )}

                <div style={{ display: callStatus !== "connected" ? "none" : "block" }}>
                  <div className="absolute inset-0 z-0 bg-black">
                    <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-contain" />
                  </div>
                  {/* Controls */}
                  <RemoteCallControls
                    isMuted={micMuted}
                    isVolumeOff={speakerMuted}
                    handleToggleMic={toggleMic}
                    handleToggleVolume={toggleSpeaker}
                    handleTakeScreenshot={handleTakeScreenshot}
                    endCall={endCall}
                  />
                </div>
                {(showScreenshotModal && (tempScreenshotBase64 || editItem)) && (
                  <ScreenshotModal
                    imageSrc={editItem ? editItem.url : tempScreenshotBase64}
                    isLoading={pageLoader}
                    initialDescription={editItem ? editItem.description : ""}
                    onClose={handleCloseModal}
                    onSave={handleSaveScreenshot}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Outlet />
    </>
  );
}

export default RemoteMonitoringView;